import React, { useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    CircularProgress,
    useTheme,
    alpha,
} from '@mui/material';
import dayjs from 'dayjs';
import {
    ChevronLeft,
    ChevronRight,
    Gauge,
    CalendarCheck,
    FlaskConical,
    Clock,
    TrendingUp,
    TrendingDown,
    Minus,
    Settings,
} from 'lucide-react';

import EficienciaHistoricoChart from './EficienciaHistoricoChart';
import ChartSkeleton from './ChartSkeleton';
import {
    LIMITE_PADRAO,
    BUFFER_PADRAO,
    corEficiencia,
    statusEficiencia,
    fmtPct,
    COR_META,
    COR_ATENCAO,
    COR_CRITICO,
} from '../utils/eficiencia';

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function KpiTile({ icon: Icon, label, value, sub, color, subColor }) {
    return (
        <Paper
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2, height: '100%', minWidth: 0 }}
        >
            <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                <Icon size={15} color={color} />
                <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ fontWeight: 600, letterSpacing: 0.2 }}
                >
                    {label}
                </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1.1 }}>
                {value}
            </Typography>
            {sub != null && (
                <Typography variant="caption" sx={{ color: subColor || 'text.secondary' }}>
                    {sub}
                </Typography>
            )}
        </Paper>
    );
}

function LegendaItem({ cor, texto }) {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
                sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: cor,
                    flexShrink: 0,
                }}
            />
            <Typography variant="caption" color="text.secondary">
                {texto}
            </Typography>
        </Stack>
    );
}

export default function SaneamentoDashboard({
    data = [],
    loading = false,
    limite = LIMITE_PADRAO,
    buffer = BUFFER_PADRAO,
    podeEditar = false,
    onSalvarLimite,
}) {
    const theme = useTheme();
    const [mesRef, setMesRef] = useState(() => dayjs().startOf('month'));

    // Diálogo de edição do limite regulatório (GESTOR/ADMIN).
    const [editOpen, setEditOpen] = useState(false);
    const [novoLimite, setNovoLimite] = useState(String(limite));
    const [salvando, setSalvando] = useState(false);

    const abrirEdicao = () => {
        setNovoLimite(String(limite ?? LIMITE_PADRAO));
        setEditOpen(true);
    };

    const limiteValido =
        novoLimite !== '' &&
        !Number.isNaN(Number(novoLimite)) &&
        Number(novoLimite) >= 0 &&
        Number(novoLimite) <= 100;

    const confirmarLimite = async () => {
        if (!limiteValido || !onSalvarLimite) return;
        setSalvando(true);
        try {
            await onSalvarLimite(Number(novoLimite));
            setEditOpen(false);
        } catch {
            // erro já sinalizado pelo chamador (toast de erro); mantém o diálogo aberto
        } finally {
            setSalvando(false);
        }
    };

    // Agrupa as análises por dia (chave YYYY-MM-DD) → média/min/max/contagem.
    const porDia = useMemo(() => {
        const grupos = new Map();
        for (const d of data || []) {
            if (!d?.dataHora || d?.eficiencia == null) continue;
            const key = dayjs(d.dataHora).format('YYYY-MM-DD');
            const arr = grupos.get(key) || [];
            arr.push(d);
            grupos.set(key, arr);
        }
        const out = new Map();
        for (const [key, items] of grupos) {
            const effs = items.map((i) => i.eficiencia).filter((v) => v != null);
            if (!effs.length) continue;
            const avg = effs.reduce((a, b) => a + b, 0) / effs.length;
            out.set(key, {
                count: items.length,
                avg,
                min: Math.min(...effs),
                max: Math.max(...effs),
            });
        }
        return out;
    }, [data]);

    // Linhas (semanas) do calendário do mês selecionado.
    const semanas = useMemo(() => {
        const inicio = mesRef.startOf('month');
        const diasNoMes = mesRef.daysInMonth();
        const celulas = [];
        for (let i = 0; i < inicio.day(); i++) celulas.push(null);
        for (let d = 1; d <= diasNoMes; d++) {
            const date = inicio.date(d);
            celulas.push({
                day: d,
                date,
                info: porDia.get(date.format('YYYY-MM-DD')) || null,
                futuro: date.isAfter(dayjs(), 'day'),
                hoje: date.isSame(dayjs(), 'day'),
            });
        }
        while (celulas.length % 7 !== 0) celulas.push(null);
        const linhas = [];
        for (let i = 0; i < celulas.length; i += 7) linhas.push(celulas.slice(i, i + 7));
        return linhas;
    }, [mesRef, porDia]);

    // KPIs do mês selecionado + tendência vs mês anterior + último ensaio global.
    const kpis = useMemo(() => {
        const dentro = (ref, k) => {
            const d = dayjs(k);
            return !d.isBefore(ref.startOf('month'), 'day') && !d.isAfter(ref.endOf('month'), 'day');
        };
        const diasMes = [...porDia.entries()].filter(([k]) => dentro(mesRef, k));
        const analisesMes = diasMes.reduce((a, [, v]) => a + v.count, 0);
        const effsMes = diasMes.map(([, v]) => v.avg);
        const mediaMes = effsMes.length
            ? effsMes.reduce((a, b) => a + b, 0) / effsMes.length
            : null;
        const totalDias = diasMes.length;
        const diasEmConformidade = diasMes.filter(([, v]) => v.avg >= limite).length;

        const refAnt = mesRef.subtract(1, 'month');
        const effsAnt = [...porDia.entries()]
            .filter(([k]) => dentro(refAnt, k))
            .map(([, v]) => v.avg);
        const mediaAnt = effsAnt.length
            ? effsAnt.reduce((a, b) => a + b, 0) / effsAnt.length
            : null;
        const tendencia = mediaMes != null && mediaAnt != null ? mediaMes - mediaAnt : null;

        const ultimo = (data || [])
            .filter((d) => d?.dataHora && d?.eficiencia != null)
            .slice()
            .sort((a, b) => dayjs(b.dataHora).valueOf() - dayjs(a.dataHora).valueOf())[0] || null;

        return { analisesMes, mediaMes, totalDias, diasEmConformidade, tendencia, ultimo };
    }, [porDia, mesRef, data, limite]);

    // Tendência recente: últimas 10 análises em ordem cronológica.
    const trendData = useMemo(
        () =>
            (data || [])
                .filter((d) => d?.dataHora && d?.eficiencia != null)
                .slice()
                .sort((a, b) => dayjs(a.dataHora).valueOf() - dayjs(b.dataHora).valueOf())
                .slice(-10),
        [data]
    );

    const podeAvancar = mesRef.startOf('month').isBefore(dayjs().startOf('month'));

    if (loading) {
        return (
            <Paper elevation={2} sx={{ borderRadius: 3, p: 2 }}>
                <ChartSkeleton height={320} />
            </Paper>
        );
    }

    const TendIcon =
        kpis.tendencia == null || Math.abs(kpis.tendencia) < 0.05
            ? Minus
            : kpis.tendencia > 0
              ? TrendingUp
              : TrendingDown;
    const tendColor =
        kpis.tendencia == null || Math.abs(kpis.tendencia) < 0.05
            ? theme.palette.text.secondary
            : kpis.tendencia > 0
              ? COR_META
              : COR_CRITICO;

    return (
        <Paper
            elevation={2}
            sx={{ borderRadius: 3, p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        >
            {/* Sem `flexWrap`: com wrap, no mobile o bloco de texto tomava a linha
                inteira e a engrenagem caia para a linha de baixo — sozinha nela, o
                `space-between` a alinhava a ESQUERDA. Agora a linha nunca quebra: o
                texto encolhe e quebra em mais linhas, e o botao fica sempre no topo
                a direita, como no desktop. */}
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                mb={2}
                gap={1}
            >
                {/* `minWidth: 0` e obrigatorio: item flex nasce com `min-width: auto`
                    e se recusaria a encolher abaixo do texto, empurrando o botao. */}
                <Box sx={{ minWidth: 0 }}>
                    {/* "de Tratamento" era redundante: o nome do cliente no topo da
                        Home ja diz qual estacao e, e um tenant = uma estacao. */}
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                        Eficiência da Estação
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                    >
                        Remoção de E.coli (Afluente → Efluente) · Limite regulatório {limite}%
                        {' '}(FEPAM/CONSEMA 355/2017)
                    </Typography>
                </Box>
                {podeEditar && (
                    <Tooltip title="Definir limite regulatório da estação">
                        <IconButton
                            size="small"
                            onClick={abrirEdicao}
                            aria-label="Definir limite regulatório"
                            // `mt` negativo compensa o padding do IconButton para o
                            // icone alinhar com o topo do titulo, nao ficar caido.
                            sx={{ flexShrink: 0, mt: -0.5 }}
                        >
                            <Settings size={18} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            {/* ── Faixa de KPIs ─────────────────────────────────────────── */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(5, 1fr)',
                    },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <KpiTile
                    icon={Gauge}
                    label="Média do mês"
                    value={fmtPct(kpis.mediaMes)}
                    sub={statusEficiencia(kpis.mediaMes, limite, buffer).label}
                    color={corEficiencia(kpis.mediaMes, limite, buffer)}
                    subColor={corEficiencia(kpis.mediaMes, limite, buffer)}
                />
                <KpiTile
                    icon={CalendarCheck}
                    label="Dias em conformidade"
                    value={`${kpis.diasEmConformidade}/${kpis.totalDias}`}
                    sub="dias com análise"
                    color={
                        kpis.totalDias && kpis.diasEmConformidade === kpis.totalDias
                            ? COR_META
                            : theme.palette.text.primary
                    }
                />
                <KpiTile
                    icon={FlaskConical}
                    label="Análises no mês"
                    value={kpis.analisesMes}
                    sub={`${MESES[mesRef.month()]}/${mesRef.year()}`}
                    color={theme.palette.text.primary}
                />
                <KpiTile
                    icon={Clock}
                    label="Último ensaio"
                    value={fmtPct(kpis.ultimo?.eficiencia)}
                    sub={
                        kpis.ultimo?.dataHora
                            ? dayjs(kpis.ultimo.dataHora).format('DD/MM HH:mm')
                            : '—'
                    }
                    color={corEficiencia(kpis.ultimo?.eficiencia, limite, buffer)}
                />
                <KpiTile
                    icon={TendIcon}
                    label="Tendência (vs mês ant.)"
                    value={
                        kpis.tendencia == null
                            ? '—'
                            : `${kpis.tendencia >= 0 ? '+' : ''}${kpis.tendencia.toFixed(1)} pp`
                    }
                    sub="pontos percentuais"
                    color={tendColor}
                />
            </Box>

            {/* ── Calendário (esq.) + Tendência recente (dir.) ──────────── */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
                {/* Calendário-heatmap */}
                <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, flex: { md: 1.15 }, minWidth: 0 }}
                >
                  <Box sx={{ maxWidth: 420, mx: 'auto', width: '100%' }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1.5}
                    >
                        <IconButton
                            size="small"
                            onClick={() => setMesRef((m) => m.subtract(1, 'month'))}
                            aria-label="Mês anterior"
                        >
                            <ChevronLeft size={18} />
                        </IconButton>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {MESES[mesRef.month()]} {mesRef.year()}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => podeAvancar && setMesRef((m) => m.add(1, 'month'))}
                            disabled={!podeAvancar}
                            aria-label="Próximo mês"
                        >
                            <ChevronRight size={18} />
                        </IconButton>
                    </Stack>

                    {/* Cabeçalho dos dias da semana */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: 0.5,
                            mb: 0.5,
                        }}
                    >
                        {DIAS_SEMANA.map((d) => (
                            <Typography
                                key={d}
                                variant="caption"
                                align="center"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                            >
                                {d}
                            </Typography>
                        ))}
                    </Box>

                    {/* Semanas */}
                    <Stack spacing={0.5}>
                        {semanas.map((semana, wi) => (
                            <Box
                                key={wi}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: 0.5,
                                }}
                            >
                                {semana.map((cel, di) => {
                                    if (!cel) return <Box key={di} sx={{ aspectRatio: '1 / 1' }} />;
                                    const temDado = !!cel.info;
                                    const cor = temDado
                                        ? corEficiencia(cel.info.avg, limite, buffer)
                                        : null;
                                    const st = statusEficiencia(cel.info?.avg, limite, buffer);

                                    // Math.round mentia em duas frentes: 99,98 virava
                                    // "100%" (100% de remocao nao existe em
                                    // microbiologia) e — pior — 84,6 virava "85%",
                                    // fazendo um dia NAO CONFORME parecer no limite.
                                    // A cor vinha de corEficiencia(84.6), ou seja
                                    // vermelha: a celula se contradizia sozinha. O
                                    // fmtPct e o mesmo do tooltip logo abaixo, que ja
                                    // mostrava o valor certo.
                                    const textoPct = temDado ? fmtPct(cel.info.avg) : '';
                                    // A celula e quadrada e estreita no mobile: "100%"
                                    // cabia folgado, "99,98%" e ">99,999%" nao. Encolhe
                                    // a fonte conforme o texto cresce, em vez de deixar
                                    // estourar.
                                    const fontePct =
                                        textoPct.length > 6
                                            ? '0.5rem'
                                            : textoPct.length > 4
                                              ? '0.54rem'
                                              : '0.6rem';
                                    const cell = (
                                        <Box
                                            sx={{
                                                aspectRatio: '1 / 1',
                                                borderRadius: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative',
                                                bgcolor: temDado
                                                    ? cor
                                                    : cel.futuro
                                                      ? 'transparent'
                                                      : alpha(theme.palette.text.disabled, 0.08),
                                                border: cel.hoje
                                                    ? `2px solid ${theme.palette.primary.main}`
                                                    : temDado
                                                      ? 'none'
                                                      : `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                                opacity: cel.futuro ? 0.4 : 1,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    lineHeight: 1,
                                                    fontWeight: temDado ? 700 : 500,
                                                    color: temDado ? '#fff' : 'text.secondary',
                                                }}
                                            >
                                                {cel.day}
                                            </Typography>
                                            {temDado && (
                                                <Typography
                                                    sx={{
                                                        fontSize: fontePct,
                                                        lineHeight: 1,
                                                        fontWeight: 700,
                                                        color: '#fff',
                                                        mt: 0.25,
                                                        whiteSpace: 'nowrap',
                                                        letterSpacing: '-0.02em',
                                                    }}
                                                >
                                                    {textoPct}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                    if (!temDado) return <Box key={di}>{cell}</Box>;
                                    return (
                                        <Tooltip
                                            key={di}
                                            arrow
                                            title={
                                                <Box sx={{ fontSize: 12, lineHeight: 1.7 }}>
                                                    <b>{cel.date.format('DD/MM/YYYY')}</b>
                                                    <br />
                                                    {st.label}
                                                    <br />
                                                    Média: {fmtPct(cel.info.avg)}
                                                    {cel.info.count > 1 && (
                                                        <>
                                                            <br />
                                                            {cel.info.count} análises ·{' '}
                                                            {fmtPct(cel.info.min)}–{fmtPct(cel.info.max)}
                                                        </>
                                                    )}
                                                    {cel.info.count === 1 && (
                                                        <>
                                                            <br />1 análise
                                                        </>
                                                    )}
                                                </Box>
                                            }
                                        >
                                            {cell}
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        ))}
                    </Stack>

                    {/* Legenda */}
                    <Stack
                        direction="row"
                        spacing={2}
                        mt={1.5}
                        flexWrap="wrap"
                        useFlexGap
                        rowGap={0.5}
                    >
                        <LegendaItem cor={COR_META} texto={`Conforme (≥ ${limite}%)`} />
                        <LegendaItem cor={COR_ATENCAO} texto="Atenção" />
                        <LegendaItem cor={COR_CRITICO} texto="Não conforme" />
                        <LegendaItem
                            cor={alpha(theme.palette.text.disabled, 0.2)}
                            texto="Sem análise"
                        />
                    </Stack>
                  </Box>
                </Paper>

                {/* Tendência recente (gráfico existente) */}
                <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
                    <EficienciaHistoricoChart
                        data={trendData}
                        loading={false}
                        limite={limite}
                    />
                </Box>
            </Stack>

            {/* ── Diálogo: definir limite regulatório (GESTOR/ADMIN) ────── */}
            <Dialog
                open={editOpen}
                onClose={salvando ? undefined : () => setEditOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Limite regulatório da estação</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Eficiência mínima de remoção (E.coli) exigida pela FEPAM para esta
                        estação, conforme a Licença de Operação (Resolução CONSEMA 355/2017).
                        Passa a valer no lugar do valor atual em todo o painel.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        type="number"
                        label="Limite regulatório"
                        value={novoLimite}
                        onChange={(e) => setNovoLimite(e.target.value)}
                        error={novoLimite !== '' && !limiteValido}
                        helperText={
                            novoLimite !== '' && !limiteValido
                                ? 'Informe um valor entre 0 e 100.'
                                : 'Valor entre 0 e 100%.'
                        }
                        InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            inputProps: { min: 0, max: 100, step: 1 },
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)} disabled={salvando} variant="text">
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmarLimite}
                        disabled={salvando || !limiteValido}
                        variant="contained"
                    >
                        {salvando ? <CircularProgress size={18} /> : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
